import { useQuery, type UseQueryOptions, type UseQueryResult } from "@tanstack/react-query";

import type {
  ImagingMetricsDashboardData,
  ImagingMetricsDashboardParams,
} from "@/features/reports/schemas/reports";
import { getImagingMetricsDashboard } from "@/features/reports/services/reports";

export const imagingMetricsKeys = {
  all: ["imaging-metrics"] as const,
  dashboard: (params: ImagingMetricsDashboardParams) =>
    [...imagingMetricsKeys.all, "dashboard", params] as const,
} as const;

/**
 * Hook para el dashboard de métricas de imagenología (reporte de negocio).
 * Endpoint: GET /api/v1/imaging/metrics/dashboard
 */
export function useImagingMetricsDashboard(
  params: ImagingMetricsDashboardParams | null,
  {
    enabled = true,
    ...options
  }: Omit<
    UseQueryOptions<ImagingMetricsDashboardData, Error>,
    "queryKey" | "queryFn"
  > = {}
): UseQueryResult<ImagingMetricsDashboardData, Error> {
  return useQuery({
    ...options,
    queryKey: imagingMetricsKeys.dashboard(
      params ?? { user_id: "", date_from: "", date_to: "" }
    ),
    queryFn: () => {
      if (!params) {
        throw new Error("Parámetros del dashboard de métricas requeridos");
      }
      return getImagingMetricsDashboard(params);
    },
    enabled: enabled && !!params?.user_id && !!params.date_from && !!params.date_to,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}
